import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { forwardRef } from '@shared/utils/forwardRef';
import { useForkRef } from '@shared/utils/hooks/useCombinedRefs';
import { useOverrides } from '@shared/utils/overrides';
import { useStyleConfig } from '@shared/joykit/packages/core/common/utils/styleConfig';
import { createDisplayName } from '@withjoy/joykit/utils';
import { useId } from '@withjoy/joykit/hooks';
import { useIsTouchDevice } from '@shared/utils/media/useIsTouchDevice';
import { useTranslation } from '@shared/core/i18n';
import { useLocaleContext } from '@localization/context/LocaleContext';
import { isTimeEqual, stringToTime, timeToString } from './time.internal';
import { useLocaleTimeFormat } from './useLocaleTimeFormat.internal';
import type { Time } from './time.types.internal';

import { Box } from '../Box';
import { Flex } from '../Flex';
import { InputV2 } from '../InputV2';
import { PopoverV2 } from '../PopoverV2';
import { Clock, Close, Check } from '@withjoy/joykit/icons';

import type { TimePickerProps } from './TimePicker.types';
import { useTimePicker } from './useTimePicker.internal';
import {
  TimePickerRoot,
  TimePickerInputWrapper,
  TimePickerOptionsList,
  TimePickerOption,
  TimePickerStylesProvider,
  timePickerStyles,
  getSelectedOptionStyles,
  iconButtonStyles,
  errorMessageStyles,
  NativeTimeInputOverlay,
  nativeInputOverlayStyles,
  DROPDOWN_SHADOW,
} from './TimePicker.styles.internal';

/**
 * Default props with explicit type widening for TypeScript inference.
 * The `as boolean` casts are intentional to allow proper type inference when
 * spreading defaults with component props. The final `as const` prevents object mutation.
 * This pattern is consistent with other JoyKit components like InputV2.
 */
export const defaultTimePickerProps = {
  format: 'auto' as const,
  minuteStep: 30 as const,
  clearable: true as boolean,
  isDisabled: false as boolean,
  isInvalid: false as boolean,
  isReadOnly: false as boolean,
  isRequired: false as boolean,
  placeholder: 'Select time' as string,
  placement: 'bottom' as const,
  overrides: {} as NonNullable<TimePickerProps['overrides']>,
} as const;

const defaultComponents = {
  Root: TimePickerRoot,
  InputWrapper: TimePickerInputWrapper,
  OptionsList: TimePickerOptionsList,
  Option: TimePickerOption,
} as const;

/**
 * A time picker component with freeform input parsing and dropdown selection.
 *
 * Features:
 * - Freeform text input with fuzzy parsing (e.g., "10p", "1012p", "22")
 * - Dropdown selection with configurable minute increments
 * - Both 12-hour (AM/PM) and 24-hour display formats
 * - Adaptive behavior based on input capability (custom picker vs native)
 * - Ref forwarding to the underlying input element
 * - Keyboard navigation with ArrowUp/Down, Enter, Escape
 *
 * @remarks
 * **Touch device behavior:**
 * Uses CSS `pointer: coarse` media query to detect touch devices, ensuring:
 * - Desktop users with narrow windows still get the full custom picker experience
 * - Actual touch devices (phones, tablets) get native platform pickers
 *
 * On touch devices (`pointer: coarse`):
 * - Uses native browser `<input type="time">` which triggers platform-specific
 *   time picker UI (iOS wheel picker, Android clock picker, etc.)
 * - The `format` prop is ignored; display format follows device locale
 * - The `placeholder` prop has no effect on native time inputs
 * - Validation uses native HTML5 constraints (`min`, `max`, `step`)
 */
const TimePicker = forwardRef<'input', TimePickerProps<Time | string>>(<T extends Time | string = Time>(baseProps: TimePickerProps<T>, ref: React.Ref<HTMLInputElement>) => {
  const props = { ...defaultTimePickerProps, ...baseProps };
  const {
    value: rawValue,
    onChange: rawOnChange,
    format,
    minuteStep,
    min,
    max,
    clearable,
    isDisabled,
    isInvalid,
    isReadOnly,
    isRequired,
    placeholder,
    placement,
    overrides,
    onFocus,
    onBlur,
    name,
    id: propsId,
    'aria-labelledby': ariaLabelledby,
    'aria-errormessage': ariaErrormessage,
  } = props;

  const generatedId = useId(propsId, 'joykit-timepicker');
  const id = propsId ?? generatedId;
  const errorId = `${id}-error`;

  const { tq } = useTranslation('timePicker');
  const { language: locale } = useLocaleContext();

  // Normalize string value to Time object for internal use
  const normalizedValue = useMemo((): Readonly<Time> | undefined => {
    if (!rawValue) return undefined;

    if (typeof rawValue === 'string') {
      // Convert string to Time - undefined if invalid
      // Existing validation in useTimePicker will show error
      return stringToTime(rawValue);
    }

    return rawValue as Readonly<Time>;
  }, [rawValue]);

  // Detect if consumer is using string mode
  const outputAsString = typeof rawValue === 'string';

  // Wrap onChange to convert back to input type
  const handleChange = useCallback(
    (time: Readonly<Time> | undefined) => {
      if (outputAsString) {
        // Convert Time back to string for string mode
        rawOnChange?.(time ? (timeToString(time) as T) : undefined);
      } else {
        rawOnChange?.(time as T | undefined);
      }
    },
    [rawOnChange, outputAsString],
  );

  // Localize default placeholder - use tq when default is used, otherwise use provided prop
  const localizedPlaceholder = placeholder === defaultTimePickerProps.placeholder ? tq`Select time` : placeholder;

  const [mounted, setMounted] = useState(false);
  const isTouchDevice = useIsTouchDevice();
  const useNativePicker = isTouchDevice;

  // Get the locale-appropriate time format (12h or 24h)
  const localeFormat = useLocaleTimeFormat();

  // Resolve 'auto' format to concrete '12h' or '24h' based on locale
  const resolvedFormat = format === 'auto' ? localeFormat : format;

  // Track mounted state to prevent SSR/hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    state,
    refs: { inputRef, listRef },
    handlers: {
      handleInputChange,
      handleInputBlur,
      handleInputKeyDown,
      handleSelectOption,
      handleClear,
      handleOpenPopover,
      handleClosePopover,
      handleTogglePopover,
      handleNativeTimeChange,
      handleOpenAutoFocus,
      setFocus,
    },
    computed: { timeOptions, nativeInputValue, nativeMinValue, nativeMaxValue, nativeStepValue, effectiveSelection },
  } = useTimePicker({
    value: normalizedValue, // Always pass Time object
    onChange: handleChange, // Wrapped to return correct type
    format: resolvedFormat,
    locale,
    minuteStep,
    min,
    max,
    clearable,
    isDisabled,
    isReadOnly,
  });

  const styles = useStyleConfig(timePickerStyles, props as TimePickerProps);
  const forkedRef = useForkRef(ref, inputRef);

  const {
    Root: [Root, rootProps],
    InputWrapper: [InputWrapper, inputWrapperProps],
    OptionsList: [OptionsList, optionsListProps],
    Option: [Option, optionProps],
  } = useOverrides(defaultComponents, overrides);

  // Handle focus events - also open the popover on desktop (unless read-only)
  const handleFocus: React.FocusEventHandler<HTMLInputElement> = e => {
    setFocus(true);
    if (!useNativePicker && !isReadOnly) {
      handleOpenPopover();
    }
    onFocus?.(e);
  };

  const handleBlurWithCallback: React.FocusEventHandler<HTMLInputElement> = e => {
    setFocus(false);
    handleInputBlur(e);
    onBlur?.(e);
  };

  // Render the clear button (shared between desktop and mobile)
  const renderClearButton = (): JSX.Element | undefined => {
    if (!clearable || !normalizedValue || isDisabled || isReadOnly) return undefined;

    return (
      <Box
        as="button"
        type="button"
        onClick={handleClear}
        onMouseDown={(e: React.MouseEvent) => e.preventDefault()} // Prevent focus steal that interferes with click
        aria-label={tq`Clear time`}
        __css={iconButtonStyles}
      >
        <Close size={16} />
      </Box>
    );
  };

  // Render the clock icon button (desktop only) - toggles popover open/closed
  // Hidden when read-only since the picker should not be interactive
  const renderClockButton = () => {
    if (isReadOnly) return null;

    return (
      <Box
        as="button"
        type="button"
        onClick={handleTogglePopover}
        onMouseDown={(e: React.MouseEvent) => e.preventDefault()} // Prevent blur before toggle
        disabled={isDisabled}
        aria-label={state.isOpen ? tq`Close time picker` : tq`Open time picker`}
        __css={iconButtonStyles}
      >
        <Clock size={16} />
      </Box>
    );
  };

  // Render a decorative (non-interactive) clock icon for mobile
  // This provides visual consistency with the desktop version
  const renderDecorativeClockIcon = () => {
    if (isReadOnly) return null;

    return (
      <Box
        as="span"
        aria-hidden
        __css={{
          ...iconButtonStyles,
          cursor: 'inherit', // Don't show pointer since it's decorative
          _hover: {}, // Remove hover styles since it's not interactive
        }}
      >
        <Clock size={16} />
      </Box>
    );
  };

  // End element with clear and clock buttons
  // Note: pointerEvents: 'auto' is required to override the 'none' value set on
  // InputV2's enhancer element, which would otherwise block clicks on the buttons
  // On mobile, position: relative and zIndex: 2 ensure the clear button sits above
  // the invisible native time input overlay (zIndex: 1) so it can receive taps
  const endElement = (
    <Flex
      alignItems="center"
      gap={1}
      paddingRight={2}
      pointerEvents="auto"
      position="relative"
      zIndex={2}
    >
      {renderClearButton()}
      {useNativePicker ? renderDecorativeClockIcon() : renderClockButton()}
    </Flex>
  );

  // Desktop input component with popover
  const DesktopInput = (
    <InputWrapper
      __css={styles.inputWrapper}
      {...inputWrapperProps}
    >
      <InputV2
        ref={forkedRef}
        id={id}
        name={name}
        type="text"
        value={state.inputValue}
        onChange={handleInputChange}
        onBlur={handleBlurWithCallback}
        onFocus={handleFocus}
        onKeyDown={handleInputKeyDown}
        isDisabled={isDisabled}
        isInvalid={isInvalid || !!state.errorMessage}
        isReadOnly={isReadOnly}
        isRequired={isRequired}
        placeholder={localizedPlaceholder}
        aria-describedby={state.errorMessage ? errorId : undefined}
        aria-labelledby={ariaLabelledby}
        aria-errormessage={ariaErrormessage}
        autoComplete="off"
        endElement={endElement}
      />
    </InputWrapper>
  );

  // Time options dropdown list
  const TimeOptionsList = (
    <OptionsList
      ref={listRef}
      role="listbox"
      aria-label={tq`Time options`}
      __css={styles.optionsList}
      {...optionsListProps}
    >
      {timeOptions.map(option => {
        // Use effectiveSelection for instant visual feedback during keyboard navigation
        const isSelected = isTimeEqual(option.value, effectiveSelection);
        return (
          <Option
            key={option.label}
            as="button"
            type="button"
            onClick={() => handleSelectOption(option)}
            onMouseDown={(e: React.MouseEvent) => e.preventDefault()} // Prevent focus steal
            role="option"
            aria-selected={isSelected}
            tabIndex={-1}
            data-time-option
            __css={{
              ...styles.option,
              ...(isSelected ? getSelectedOptionStyles() : {}),
            }}
            {...optionProps}
          >
            <Box
              as="span"
              flex={1}
              textAlign="left"
            >
              {option.label}
            </Box>
            {isSelected && <Check size={16} />}
          </Option>
        );
      })}
    </OptionsList>
  );

  // Native time input for mobile
  // Uses InputV2 for visual consistency with a transparent native input overlay
  // that captures taps to trigger the native picker UI
  const NativeTimeInput = (
    <InputWrapper
      __css={styles.inputWrapper}
      {...inputWrapperProps}
    >
      <NativeTimeInputOverlay
        position="relative"
        width="100%"
      >
        <InputV2
          ref={forkedRef}
          id={id}
          name={name}
          type="text"
          value={state.inputValue}
          isDisabled={isDisabled}
          isInvalid={isInvalid || !!state.errorMessage}
          isReadOnly
          isRequired={isRequired}
          placeholder={localizedPlaceholder}
          aria-describedby={state.errorMessage ? errorId : undefined}
          aria-labelledby={ariaLabelledby}
          aria-errormessage={ariaErrormessage}
          autoComplete="off"
          endElement={endElement}
        />
        {/* Invisible native time input overlay to trigger native picker on tap */}
        {!isDisabled && !isReadOnly && (
          <Box
            as="input"
            type="time"
            value={nativeInputValue}
            onChange={handleNativeTimeChange}
            onFocus={handleFocus}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              setFocus(false);
              onBlur?.(e);
            }}
            min={nativeMinValue}
            max={nativeMaxValue}
            step={nativeStepValue}
            aria-hidden="true"
            tabIndex={-1}
            __css={nativeInputOverlayStyles}
          />
        )}
      </NativeTimeInputOverlay>
    </InputWrapper>
  );

  // Pre-hydration render: show InputV2 skeleton to prevent visual flash
  // Include endElement to prevent clock icon flickering on mount
  if (!mounted) {
    // Pre-hydration end element - includes clock icon but no clear button
    // (clear button depends on runtime value state)
    // Hide clock icon if isReadOnly since picker is not interactive
    const preHydrationEndElement = isReadOnly ? undefined : (
      <Flex
        alignItems="center"
        gap={1}
        paddingRight={2}
        pointerEvents="auto"
      >
        <Box
          as="span"
          aria-hidden
          __css={iconButtonStyles}
        >
          <Clock size={16} />
        </Box>
      </Flex>
    );

    return (
      <TimePickerStylesProvider value={styles}>
        <Root
          data-slot="time-picker"
          __css={styles.root}
          {...rootProps}
        >
          <InputWrapper
            __css={styles.inputWrapper}
            {...inputWrapperProps}
          >
            <InputV2
              id={id}
              name={name}
              type="text"
              value={state.inputValue}
              isDisabled={isDisabled}
              isInvalid={isInvalid}
              isReadOnly
              placeholder={localizedPlaceholder}
              autoComplete="off"
              endElement={preHydrationEndElement}
            />
          </InputWrapper>
        </Root>
      </TimePickerStylesProvider>
    );
  }

  return (
    <TimePickerStylesProvider value={styles}>
      <Root
        data-slot="time-picker"
        __css={styles.root}
        {...rootProps}
      >
        {useNativePicker ? (
          NativeTimeInput
        ) : (
          <PopoverV2
            isOpen={state.isOpen}
            onClose={handleClosePopover}
            onOpen={handleOpenPopover}
            placement={placement}
            triggerType="none"
            showArrow={false}
            disableCloseOnEscapeKeyClick={false}
          >
            <PopoverV2.Anchor>{DesktopInput}</PopoverV2.Anchor>
            <PopoverV2.Content
              minWidth="220px"
              onOpenAutoFocus={handleOpenAutoFocus}
              boxShadow={DROPDOWN_SHADOW}
            >
              {TimeOptionsList}
            </PopoverV2.Content>
          </PopoverV2>
        )}

        {/* Error message display */}
        {state.errorMessage && (
          <Box
            id={errorId}
            role="alert"
            aria-live="polite"
            __css={errorMessageStyles}
          >
            {state.errorMessage}
          </Box>
        )}
      </Root>
    </TimePickerStylesProvider>
  );
}) as (<T extends Time | string = Time>(props: TimePickerProps<T> & { ref?: React.Ref<HTMLInputElement> }) => React.ReactElement | null) & { displayName?: string };

TimePicker.displayName = createDisplayName('TimePicker');

export { TimePicker };
